// Parents approval requests list

import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { ParentStatuses } from '../utils/enums';
import { ParentStatus } from './PlayersList';
import { useModal } from './ModalProvider';
import { userSetParentStatus } from '../utils/firebase';
import { useEntities } from '../utils/hooks';
import MultiSelectInline from './MultiSelectInline';
import Spinner from './Spinner';
import Avatar from './Avatar';
import './ParentsList.scss';

export default function ParentsList({ coachId }) {
  const { usersById, teamsById, parentsByPlayerId, isParentsLoaded } =
    useFirebase();
  const { openModal } = useModal();

  // Whose links these are is on the link document — it carries the coaches of
  // the player's teams, which is exactly the question this list asks. Walking
  // `users` to find out, as this did, meant a player whose document had not
  // resolved silently dropped out of the approval list.
  const links = useMemo(() => {
    const result = [];
    for (const [playerId, parents] of parentsByPlayerId) {
      for (const link of parents) {
        if (!link.coachIds?.includes(coachId)) continue;
        result.push({ playerId, link });
      }
    }
    return result;
  }, [parentsByPlayerId, coachId]);

  // The names, avatars and contacts of everyone on those links. Bounded by the
  // coach's own roster, so it is a by-id read rather than a list (P3/P9).
  const peopleIds = useMemo(() => {
    const ids = new Set();
    for (const { playerId, link } of links) {
      ids.add(playerId);
      ids.add(link.id);
    }
    return [...ids];
  }, [links]);
  const { isResolved: arePeopleResolved } = useEntities('users', peopleIds);
  // A row is one player on one of this coach's teams, so the teams are read by
  // id too — the organization-wide list they used to come off is gone
  const coachTeamIds = useMemo(
    () => usersById.get(coachId)?.teams ?? [],
    [usersById, coachId],
  );
  const { isResolved: areTeamsResolved } = useEntities('teams', coachTeamIds);
  // The links come first and the people on them second — before the links land
  // there is nobody to resolve, so `arePeopleResolved` alone reads as "no
  // approvals" rather than as "not read yet"
  const isReady = isParentsLoaded && arePeopleResolved && areTeamsResolved;

  const [overriddenStatuses, setOverriddenStatuses] = useState({});
  const [spinner, setSpinner] = useState(false);
  const [spinnerKey, setSpinnerKey] = useState(null);
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  const bulkApproveBtnRef = useRef(null);

  // Build flat list of pending parent entries for coach's teams
  const initialEntries = useRef(null);

  const entries = useMemo(() => {
    const coach = usersById.get(coachId);
    if (!coach || !isReady) return [];

    const coachTeamIds = coach.teams ?? [];

    const result = [];

    for (const { playerId, link } of links) {
      const player = usersById.get(playerId);
      const parentUser = usersById.get(link.id);
      if (!player || !parentUser) continue;

      // The link carries the player's teams; only the ones this coach runs
      // belong on their card
      const playerTeams = (link.teamIds ?? [])
        .filter((teamId) => coachTeamIds.includes(teamId))
        .map((teamId) => teamsById.get(teamId))
        .filter(Boolean);

      for (const team of playerTeams) {
        result.push({
          key: `${link.id}_${playerId}_${team.id}`,
          parent: parentUser,
          player,
          team,
          status: link.status,
        });
      }
    }

    result.sort((a, b) => {
      const teamCmp = a.team.name.localeCompare(b.team.name, 'en', {
        sensitivity: 'base',
      });
      if (teamCmp !== 0) return teamCmp;
      return a.parent.name.localeCompare(b.parent.name, 'en', {
        sensitivity: 'base',
      });
    });

    return result;
  }, [usersById, teamsById, links, coachId, isReady]);

  // Capture initial pending keys on first render
  if (initialEntries.current === null && entries.length > 0) {
    initialEntries.current = new Set(
      entries
        .filter((e) => e.status === ParentStatuses.PENDING)
        .map((e) => e.key),
    );
  }

  // Filter to only show entries that were pending on mount
  const displayedEntries = useMemo(() => {
    if (!initialEntries.current) return [];
    return entries.filter((e) => initialEntries.current.has(e.key));
  }, [entries]);

  const getStatus = (entry) => overriddenStatuses[entry.key] ?? entry.status;

  const pendingEntries = displayedEntries.filter(
    (e) => getStatus(e) === ParentStatuses.PENDING,
  );

  const getEntryLabel = (e) =>
    `${e.parent.name} → ${e.player.name} (${e.team.name})`;

  const handleBulkApprove = async (selectedKeys) => {
    const selected = pendingEntries.filter((e) =>
      selectedKeys.includes(e.key),
    );
    if (selected.length === 0) return;

    setSpinner(true);
    let approvedCount = 0;
    try {
      for (const entry of selected) {
        try {
          await userSetParentStatus({
            parentId: entry.parent.id,
            playerId: entry.player.id,
            status: ParentStatuses.APPROVED,
          });
          setOverriddenStatuses((prev) => ({
            ...prev,
            [entry.key]: ParentStatuses.APPROVED,
          }));
          approvedCount++;
        } catch (error) {
          toast.error(
            error?.message ??
              `Failed to approve ${entry.parent.name} → ${entry.player.name}`,
          );
        }
      }
      if (approvedCount > 0) {
        toast.success(`Approved ${approvedCount} parent(s)`);
      }
    } finally {
      setSpinner(false);
    }
  };

  const handleApprove = async (entry) => {
    try {
      setSpinner(true);
      setSpinnerKey(entry.key);
      await userSetParentStatus({
        parentId: entry.parent.id,
        playerId: entry.player.id,
        status: ParentStatuses.APPROVED,
      });
      setOverriddenStatuses((prev) => ({
        ...prev,
        [entry.key]: ParentStatuses.APPROVED,
      }));
    } catch (error) {
      toast.error(error?.message ?? 'Failed to approve parent');
    } finally {
      setSpinner(false);
      setSpinnerKey(null);
    }
  };

  const handleReject = (entry) => {
    const onReject = async () => {
      try {
        setSpinner(true);
        setSpinnerKey(entry.key);
        await userSetParentStatus({
          parentId: entry.parent.id,
          playerId: entry.player.id,
          status: ParentStatuses.REJECTED,
        });
        setOverriddenStatuses((prev) => ({
          ...prev,
          [entry.key]: ParentStatuses.REJECTED,
        }));
      } catch (error) {
        toast.error(error?.message ?? 'Failed to reject parent');
      } finally {
        setSpinner(false);
        setSpinnerKey(null);
      }
    };

    openModal({
      header: 'Reject parent',
      body: (
        <p className='font-16'>
          {'Are you sure you want to reject '}
          <span className='font-medium'>{entry.parent.name}</span>
          {' as a parent of '}
          <span className='font-medium'>{entry.player.name}</span>
          {'?'}
        </p>
      ),
      buttons: [
        { text: 'Cancel', className: 'bw' },
        { text: 'Reject', className: 'danger', onClick: onReject },
      ],
    });
  };

  const handleCancel = async (entry) => {
    try {
      setSpinner(true);
      setSpinnerKey(entry.key);
      await userSetParentStatus({
        parentId: entry.parent.id,
        playerId: entry.player.id,
        status: ParentStatuses.PENDING,
      });
      setOverriddenStatuses((prev) => {
        const next = { ...prev };
        delete next[entry.key];
        return next;
      });
    } catch (error) {
      toast.error(error?.message ?? 'Failed to cancel');
    } finally {
      setSpinner(false);
      setSpinnerKey(null);
    }
  };

  return (
    <div className='parents-list'>
      <Spinner show={spinner} />

      <div className='sticky-top'>
        <div className='controls'>
          <button
            className='small bw'
            onClick={() => setShowBulkApprove(true)}
            ref={bulkApproveBtnRef}
            disabled={pendingEntries.length === 0}
          >
            Bulk approve
          </button>
          <MultiSelectInline
            header='Select parents to approve'
            options={pendingEntries.map((e) => ({
              value: e.key,
              label: getEntryLabel(e),
            }))}
            value={[]}
            small
            show={showBulkApprove}
            onChange={handleBulkApprove}
            onClose={() => setShowBulkApprove(false)}
            anchorRef={bulkApproveBtnRef}
          />
        </div>

        <ul className='tab-header'>
          <li className='col-1' />
          <li className='col-2'>Parent</li>
          <li className='col-25'>Parent, Status</li>
          <li className='col-3'>Player</li>
          <li className='col-4'>Team</li>
          <li className='col-34'>Player, Team</li>
          <li className='col-5'>Status</li>
        </ul>
      </div>

      <div className='tab-body'>
        {!isReady && <Spinner show inline />}
        {displayedEntries.map((entry) => {
          const status = getStatus(entry);
          const isPending = status === ParentStatuses.PENDING;
          const isLoading = spinner && spinnerKey === entry.key;

          const statusBlock = (
            <>
              <ParentStatus status={status} />
              {isPending ? (
                <div className='btns-wrap'>
                  <button
                    className='small bw'
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      handleApprove(entry);
                    }}
                  >
                    Approve
                  </button>
                  <button
                    className='small danger'
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      handleReject(entry);
                    }}
                  >
                    Reject
                  </button>
                </div>
              ) : (
                <div className='btns-wrap'>
                  <button
                    className='small bw'
                    disabled={isLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      handleCancel(entry);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          );

          return (
            <Link
              key={entry.key}
              to={'/parents/' + entry.parent.id}
              className='parent-row'
            >
              <Avatar user={entry.parent} size={64} className='col-1' />
              <div className='col-2'>
                <p className='upper font-medium'>{entry.parent.name}</p>
                <p>{entry.parent.email}</p>
                <p>{entry.parent.phone}</p>
              </div>
              <div className='col-25'>
                <div className='img-name-wrap'>
                  <Avatar user={entry.parent} size={32} />
                  <p className='upper font-medium'>{entry.parent.name}</p>
                </div>
                <p>{entry.parent.email}</p>
                <p>{entry.parent.phone}</p>
                {statusBlock}
              </div>
              <p className='col-3 font-medium'>{entry.player.name}</p>
              <p className='col-4'>{entry.team.name}</p>
              <div className='col-34'>
                <p className='font-medium'>{entry.player.name}</p>
                <p>{entry.team.name}</p>
              </div>
              <div className='col-5'>{statusBlock}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
